import React from 'react';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';

interface IPaginacaoProps {
  paginaAtual: number;
  totalPaginas: number;
  totalItens: number;
  itensPorPagina: number;
  opcoesItensPorPagina?: number[];
  onMudarPagina: (pagina: number) => void;
  onMudarItensPorPagina: (quantidade: number) => void;
}

// Números exibidos na paginação: sempre a primeira, a última e as vizinhas da atual, com reticências entre os saltos
function paginasVisiveis(paginaAtual: number, totalPaginas: number): (number | '...')[] {
  if (totalPaginas <= 7) return Array.from({ length: totalPaginas }, (_, i) => i + 1);

  const paginas = new Set([1, totalPaginas, paginaAtual - 1, paginaAtual, paginaAtual + 1]);
  const ordenadas = Array.from(paginas).filter(pagina => pagina >= 1 && pagina <= totalPaginas).sort((a, b) => a - b);

  return ordenadas.reduce<(number | '...')[]>((resultado, pagina, i) => {
    if (i > 0 && pagina - ordenadas[i - 1] > 1) resultado.push('...');
    resultado.push(pagina);
    return resultado;
  }, []);
}

const classeBotaoNavegacao = 'flex h-10 w-10 items-center justify-center rounded-full text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:text-zinc-300 disabled:hover:bg-transparent';

export default function Paginacao({
  paginaAtual,
  totalPaginas,
  totalItens,
  itensPorPagina,
  opcoesItensPorPagina = [10, 25, 50],
  onMudarPagina,
  onMudarItensPorPagina,
}: IPaginacaoProps) {
  const primeiroItem = totalItens === 0 ? 0 : (paginaAtual - 1) * itensPorPagina + 1;
  const ultimoItem = Math.min(paginaAtual * itensPorPagina, totalItens);

  return (
    <nav aria-label='Paginação' className='flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 px-4 py-3 text-sm text-zinc-500'>
      <div className='flex items-center gap-2'>
        <label htmlFor='itensPorPagina' className='hidden sm:inline'>Itens por página</label>
        <select
          id='itensPorPagina'
          value={itensPorPagina}
          onChange={(e) => onMudarItensPorPagina(+e.target.value)}
          aria-label='Itens por página'
          className='h-9 rounded-lg border border-zinc-200 bg-white px-2 text-sm text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10'
        >
          {opcoesItensPorPagina.map(opcao => <option key={opcao} value={opcao}>{opcao}</option>)}
        </select>
        <span aria-live='polite'>{primeiroItem}–{ultimoItem} de {totalItens}</span>
      </div>

      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={() => onMudarPagina(paginaAtual - 1)}
          disabled={paginaAtual <= 1}
          aria-label='Página anterior'
          className={classeBotaoNavegacao}
        >
          <AiOutlineLeft className='h-4 w-4' aria-hidden='true' />
        </button>

        <span className='px-2 text-zinc-700 md:hidden'>Página {paginaAtual} de {totalPaginas}</span>

        <div className='hidden items-center gap-1 md:flex'>
          {paginasVisiveis(paginaAtual, totalPaginas).map((pagina, i) => pagina === '...' ? (
            <span key={`reticencias-${i}`} className='w-6 text-center text-zinc-400' aria-hidden='true'>…</span>
          ) : (
            <button
              key={pagina}
              type='button'
              onClick={() => onMudarPagina(pagina)}
              aria-current={pagina === paginaAtual ? 'page' : undefined}
              aria-label={`Página ${pagina}`}
              className={`h-9 min-w-[36px] rounded-full px-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 ${pagina === paginaAtual ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}
            >
              {pagina}
            </button>
          ))}
        </div>

        <button
          type='button'
          onClick={() => onMudarPagina(paginaAtual + 1)}
          disabled={paginaAtual >= totalPaginas}
          aria-label='Próxima página'
          className={classeBotaoNavegacao}
        >
          <AiOutlineRight className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
    </nav>
  );
}
